import { useProviders, useDeleteProvider } from '../../hooks/useProviders';
import { Loader2, AlertCircle, Plus, Edit, Trash2, ExternalLink, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { IdentityProvider } from '../../types';
import api from '../../services/api';
import { appConfig } from '../../config/app';
import { getProviderLogo } from '../../utils/providerLogos';

export const ProviderList = () => {
  const { data: providers, isLoading, error } = useProviders();
  const deleteProvider = useDeleteProvider();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  // Calculate callback URL (API base URL + /auth/callback)
  // Remove /api and any trailing slashes to avoid double slashes
  const baseUrl = appConfig.apiBaseUrl.replace('/api', '').replace(/\/+$/, '');
  const callbackUrl = `${baseUrl}/auth/callback`;

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(callbackUrl);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handleDelete = async (providerId: string) => {
    if (!confirm('Are you sure you want to delete this provider and all associated tokens?')) {
      return;
    }

    try {
      setDeletingId(providerId);
      await deleteProvider.mutateAsync(providerId);
    } catch (error) {
      console.error('Failed to delete provider:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleConnect = async (provider: IdentityProvider) => {
    try {
      if (!provider.isActive || provider.templateIsActive === false) {
        alert('This provider is inactive (or its template is inactive) and cannot be connected. Please reconfigure.');
        return;
      }
      setConnectingId(provider.id);

      // Call API endpoint with JWT authentication
      // API returns JSON with authorization URL
      const response = await api.get<{ authorizationUrl: string }>(`/auth/authorize`, {
        params: { providerId: provider.id }
      });

      // Redirect to provider's authorization page
      if (response.data?.authorizationUrl) {
        window.location.href = response.data.authorizationUrl;
      } else {
        throw new Error('No authorization URL received from API');
      }
    } catch (error) {
      console.error('Failed to initiate OAuth flow:', error);
      alert('Failed to connect to provider. Please try again.');
      setConnectingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Providers</h3>
          <p className="text-gray-600">{(error as any).message || 'Failed to load providers'}</p>
        </div>
      </div>
    );
  }

  // Visibility rules:
  // - Hide providers with no template that are inactive.
  // - Hide providers where both provider and template are inactive.
  // - Show inactive providers if their template is still active (so user can reconfigure).
  // - Show active providers even if their template is inactive (but mark as "Template inactive").
  const visibleProviders = providers?.filter((p) => p.isActive || p.templateIsActive === true) ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Identity Providers</h2>
          <p className="text-gray-600 mt-1">Configure your OAuth provider connections</p>
        </div>
        <Link to="/providers/new" className="btn btn-primary px-4 py-2 flex items-center space-x-2">
          <Plus className="h-4 w-4" />
          <span>Add Provider</span>
        </Link>
      </div>

      {/* Callback URL Info Box */}
      <div className="mb-6 card p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-blue-900 mb-1">OAuth Redirect URI</h3>
            <p className="text-sm text-blue-700 mb-2">
              Use this URL as the redirect/callback URI when configuring your OAuth application in the external provider (Autodesk, etc.):
            </p>
            <div className="flex items-center space-x-2">
              <code className="flex-1 px-3 py-2 bg-white border border-blue-300 rounded text-sm text-blue-900 font-mono">
                {callbackUrl}
              </code>
              <button
                onClick={handleCopyUrl}
                className="btn btn-outline px-3 py-2 flex items-center space-x-2 text-blue-700 border-blue-300 hover:bg-blue-100"
                title="Copy to clipboard"
              >
                {copiedUrl ? (
                  <>
                    <Check className="h-4 w-4" />
                    <span className="text-sm">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    <span className="text-sm">Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {visibleProviders.length === 0 ? (
        <div className="text-center py-12 card">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Providers Configured</h3>
          <p className="text-gray-600 mb-4">
            Add an identity provider to start managing tokens.
          </p>
          <Link to="/providers/new" className="btn btn-primary px-6 py-2">
            Add Your First Provider
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {visibleProviders.map((provider) => (
            <div key={provider.id} className="card p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-3 flex-1">
                  {getProviderLogo(provider.providerType, 'md')}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{provider.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {provider.providerType}
                      {provider.templateName && <span> • {provider.templateName}</span>}
                    </p>
                    {provider.isCustomProvider && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-2">
                        Custom Provider
                      </span>
                    )}
                  </div>
                </div>
                {provider.templateIsActive === false ? (
                  <span
                    className="px-2 py-1 text-xs font-medium rounded bg-orange-100 text-orange-800"
                    title="Template is inactive/deleted; reconfigure this provider"
                  >
                    Template inactive
                  </span>
                ) : (
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded ${
                      provider.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {provider.isActive ? 'Active' : 'Inactive'}
                  </span>
                )}
              </div>

              <div className="text-sm text-gray-600 mb-4">
                <div>Client ID: {provider.clientId}</div>
                {provider.scopes && (
                  <div className="mt-1">
                    Scopes: {Array.isArray(provider.scopes) ? provider.scopes.join(', ') : provider.scopes}
                  </div>
                )}
              </div>

              <div className="flex space-x-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleConnect(provider)}
                  disabled={
                    connectingId === provider.id ||
                    !provider.isActive ||
                    provider.templateIsActive === false
                  }
                  className="btn btn-primary px-4 py-2 flex items-center space-x-2 flex-1 disabled:opacity-50"
                >
                  {connectingId === provider.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4" />
                  )}
                  <span>{connectingId === provider.id ? 'Connecting...' : 'Connect'}</span>
                </button>

                <Link
                  to={`/providers/${provider.id}/edit`}
                  className="btn btn-outline px-4 py-2 flex items-center space-x-2"
                >
                  <Edit className="h-4 w-4" />
                </Link>

                <button
                  onClick={() => handleDelete(provider.id)}
                  disabled={deletingId === provider.id}
                  className="btn btn-outline px-4 py-2 flex items-center space-x-2 text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
